import { bookmarkAPI } from '../../utils/bookmarkAPI';

// Max ids per messages lookup; the backend caps its page size at 50.
const BOOKMARK_BATCH_SIZE = 50;

// Action Types
export const BOOKMARK_ACTIONS = {
	LOAD_BOOKMARKS_START: 'LOAD_BOOKMARKS_START',
	LOAD_BOOKMARKS_SUCCESS: 'LOAD_BOOKMARKS_SUCCESS',
	LOAD_BOOKMARKS_ERROR: 'LOAD_BOOKMARKS_ERROR',
	ADD_BOOKMARK_START: 'ADD_BOOKMARK_START',
	ADD_BOOKMARK_SUCCESS: 'ADD_BOOKMARK_SUCCESS',
	ADD_BOOKMARK_ERROR: 'ADD_BOOKMARK_ERROR',
	REMOVE_BOOKMARK_START: 'REMOVE_BOOKMARK_START',
	REMOVE_BOOKMARK_SUCCESS: 'REMOVE_BOOKMARK_SUCCESS',
	REMOVE_BOOKMARK_ERROR: 'REMOVE_BOOKMARK_ERROR',
	CLEAR_ERROR: 'CLEAR_ERROR',
};

// Initial State
const initialState = {
	bookmarkIds: [], // Array of message IDs that are bookmarked
	bookmarkedMessages: [], // Array of full message objects
	loading: false,
	error: null,
	initialized: false, // Track if bookmarks have been loaded initially
	messagesFailed: false, // The message lookup failed, so nothing is known about hidden ones
	hiddenCount: 0, // Saved messages that exist but are older than the free window
};

// Reducer
export const bookmarkReducer = (state = initialState, action) => {
	switch (action.type) {
		case BOOKMARK_ACTIONS.LOAD_BOOKMARKS_START:
			return {
				...state,
				loading: true,
				error: null,
			};

		case BOOKMARK_ACTIONS.LOAD_BOOKMARKS_SUCCESS:
			return {
				...state,
				loading: false,
				bookmarkIds: action.payload.bookmarkIds,
				bookmarkedMessages: action.payload.messages,
				messagesFailed: Boolean(action.payload.messagesFailed),
				hiddenCount: action.payload.hiddenCount || 0,
				initialized: true,
				error: null,
			};

		case BOOKMARK_ACTIONS.LOAD_BOOKMARKS_ERROR:
			return {
				...state,
				loading: false,
				error: action.payload,
				initialized: true,
			};

		case BOOKMARK_ACTIONS.ADD_BOOKMARK_START:
			return {
				...state,
				loading: true,
				error: null,
			};

		case BOOKMARK_ACTIONS.ADD_BOOKMARK_SUCCESS:
			return {
				...state,
				loading: false,
				bookmarkIds: [...state.bookmarkIds, action.payload.messageId],
				bookmarkedMessages: [
					...state.bookmarkedMessages,
					action.payload.message,
				],
				error: null,
			};

		case BOOKMARK_ACTIONS.ADD_BOOKMARK_ERROR:
			return {
				...state,
				loading: false,
				error: action.payload,
			};

		case BOOKMARK_ACTIONS.REMOVE_BOOKMARK_START:
			return {
				...state,
				loading: true,
				error: null,
			};

		case BOOKMARK_ACTIONS.REMOVE_BOOKMARK_SUCCESS:
			return {
				...state,
				loading: false,
				bookmarkIds: state.bookmarkIds?.filter(
					(id) => id !== action.payload.messageId
				),
				bookmarkedMessages: state.bookmarkedMessages?.filter(
					(message) => message.messageId !== action.payload.messageId
				),
				error: null,
			};

		case BOOKMARK_ACTIONS.REMOVE_BOOKMARK_ERROR:
			return {
				...state,
				loading: false,
				error: action.payload,
			};

		case BOOKMARK_ACTIONS.CLEAR_ERROR:
			return {
				...state,
				error: null,
			};

		default:
			return state;
	}
};

// Action Creators (Thunks for async operations)
export const bookmarkActions = {
	// Load all bookmarks on app initialization
	loadBookmarks: (userId) => async (dispatch) => {
		dispatch({ type: BOOKMARK_ACTIONS.LOAD_BOOKMARKS_START });

		try {
			// Get bookmark IDs
			const bookmarkResponse = await bookmarkAPI.getBookmarks();
			const bookmarkIds = bookmarkResponse.bookmarks || [];

			// If there are bookmarks, fetch the full message data. The backend caps
			// each lookup at 50 messages, so ask in batches of 50 and merge.
			let messages = [];
			let messagesFailed = false;
			let hiddenCount = 0;
			if (bookmarkIds.length > 0) {
				try {
					const batches = [];
					for (let i = 0; i < bookmarkIds.length; i += BOOKMARK_BATCH_SIZE) {
						batches.push(bookmarkIds.slice(i, i + BOOKMARK_BATCH_SIZE));
					}
					const responses = await Promise.all(
						batches.map((ids) => bookmarkAPI.getMessagesAPI(ids))
					);
					messages = responses.flatMap((r) => r.messages || []);
					// Free viewers: the backend says how many saved messages it held
					// back for being older than their window (absent for pro).
					hiddenCount = responses.reduce(
						(sum, r) => sum + (r.pagination?.olderCount || 0),
						0
					);
				} catch (msgError) {
					console.error('Failed to fetch bookmark messages:', msgError);
					// bookmarkIds are still valid — dispatch success with IDs only
					messages = [];
					messagesFailed = true;
				}
			}
			dispatch({
				type: BOOKMARK_ACTIONS.LOAD_BOOKMARKS_SUCCESS,
				payload: {
					bookmarkIds,
					messages,
					messagesFailed,
					hiddenCount,
				},
			});
		} catch (error) {
			dispatch({
				type: BOOKMARK_ACTIONS.LOAD_BOOKMARKS_ERROR,
				payload: error.message,
			});
		}
	},

	// Add a bookmark
	addBookmark:
		(userId, messageId, messageData) => async (dispatch, getState) => {
			dispatch({ type: BOOKMARK_ACTIONS.ADD_BOOKMARK_START });

			try {
				// Check if already bookmarked
				const { bookmarkIds } = getState().bookmarks;
				if (bookmarkIds.includes(messageId)) {
					dispatch({
						type: BOOKMARK_ACTIONS.ADD_BOOKMARK_ERROR,
						payload: 'Message is already bookmarked',
					});
					return;
				}

				// Add bookmark via API
				await bookmarkAPI.addBookmark(messageId);

				dispatch({
					type: BOOKMARK_ACTIONS.ADD_BOOKMARK_SUCCESS,
					payload: {
						messageId,
						message: messageData,
					},
				});
			} catch (error) {
				dispatch({
					type: BOOKMARK_ACTIONS.ADD_BOOKMARK_ERROR,
					payload: error.message,
				});
			}
		},

	// Remove a bookmark
	removeBookmark: (userId, messageId) => async (dispatch) => {
		dispatch({ type: BOOKMARK_ACTIONS.REMOVE_BOOKMARK_START });

		try {
			// Remove bookmark via API
			await bookmarkAPI.removeBookmark(messageId);

			dispatch({
				type: BOOKMARK_ACTIONS.REMOVE_BOOKMARK_SUCCESS,
				payload: {
					messageId,
				},
			});
		} catch (error) {
			dispatch({
				type: BOOKMARK_ACTIONS.REMOVE_BOOKMARK_ERROR,
				payload: error.message,
			});
		}
	},

	// Toggle bookmark (add if not present, remove if present)
	toggleBookmark:
		(userId, messageId, messageData) => async (dispatch, getState) => {
			const { bookmarkIds } = getState().bookmarks;

			if (bookmarkIds.includes(messageId)) {
				dispatch(bookmarkActions.removeBookmark(userId, messageId));
			} else {
				dispatch(bookmarkActions.addBookmark(userId, messageId, messageData));
			}
		},

	// Clear error
	clearError: () => ({
		type: BOOKMARK_ACTIONS.CLEAR_ERROR,
	}),
};

export const bookmarkSelectors = {
	getBookmarkIds: (state) => state?.bookmarks?.bookmarkIds,

	getBookmarkedMessages: (state) => state?.bookmarks?.bookmarkedMessages,

	isBookmarked: (state, messageId) =>
		state?.bookmarks?.bookmarkIds?.includes(messageId),

	isLoading: (state) => state?.bookmarks?.loading,

	getError: (state) => state?.bookmarks?.error,

	isInitialized: (state) => state?.bookmarks?.initialized,

	// Saved messages that exist but are older than the free window, as counted
	// by the backend (pagination.olderCount); 0 for pro and when the lookup failed.
	getHiddenBookmarkCount: (state) =>
		state?.bookmarks?.messagesFailed ? 0 : state?.bookmarks?.hiddenCount || 0,

	getBookmarkCount: (state) => state?.bookmarks?.bookmarkIds?.length,

	getBookmarkedMessage: (state, messageId) =>
		state?.bookmarks?.bookmarkedMessages?.find(
			(message) => message.messageId === messageId
		),
};
